import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle, ArrowRight, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const pkgParam = params.get("package") || "basic";
  const [state, setState] = useState("verifying");
  const [details, setDetails] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setDetails(data);
          setState("paid");
          return;
        }
      } catch {
        if (attempts > 5) {
          if (!cancelled) setState("error");
          return;
        }
      }
      attempts += 1;
      if (attempts < 20) setTimeout(poll, 2000);
      else if (!cancelled) setState("timeout");
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const pkg = details?.package || pkgParam;

  return (
    <main className="relative flex min-h-screen items-center justify-center overflow-hidden px-4 pt-20 pb-16" data-testid="payment-success-page">
      <div className="w-full max-w-2xl">
        {state === "verifying" && (
          <div className="rounded-3xl border border-white/10 bg-white/5 p-10 text-center backdrop-blur" data-testid="payment-verifying">
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-blue-400" />
            <h1 className="mt-5 font-display text-2xl font-bold text-white">Confirming your payment…</h1>
            <p className="mt-2 text-sm text-slate-400">Hold tight — we're checking with Stripe.</p>
          </div>
        )}
        {state === "paid" && (
          <div className="rounded-3xl border border-white/10 bg-white/5 p-10 text-center backdrop-blur" data-testid="payment-confirmed">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/15">
              <CheckCircle2 className="h-9 w-9 text-emerald-400" />
            </span>
            <h1 className="mt-6 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Payment confirmed. Welcome aboard.
            </h1>
            <p className="mx-auto mt-3 max-w-md text-lg text-slate-400">
              Your {details?.package_name || "website"} package is booked
              {details?.monthly_name ? ` with ${details.monthly_name} ($${details.amount_monthly}/mo, first charge in ${details.trial_days || 30} days)` : ""}.
            </p>
            <div className="mx-auto mt-8 max-w-md rounded-2xl border border-blue-400/30 bg-blue-500/10 p-5 text-left">
              <p className="flex items-center gap-2 text-sm font-semibold text-white">
                <Mail className="h-4 w-4 text-blue-400" /> Check your inbox
              </p>
              <p className="mt-2 text-sm leading-relaxed text-slate-300">
                We've emailed a link to your website request form to your checkout email. You can also start right
                now:
              </p>
              <Link
                to={`/get-started/${pkg}`}
                data-testid="success-form-btn"
                className="group mt-4 flex items-center justify-center gap-2 rounded-full bg-blue-600 py-3 text-sm font-semibold text-white transition-colors hover:bg-blue-500"
              >
                Complete Your Website Request Form
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
            <Link
              to="/"
              data-testid="success-home-btn"
              className="mt-7 inline-block text-sm font-semibold text-slate-400 transition-colors hover:text-white"
            >
              Back to Home
            </Link>
          </div>
        )}
        {(state === "error" || state === "timeout") && (
          <div className="rounded-3xl border border-white/10 bg-white/5 p-10 text-center backdrop-blur" data-testid="payment-unconfirmed">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-500/15">
              <XCircle className="h-9 w-9 text-amber-400" />
            </span>
            <h1 className="mt-6 font-display text-2xl font-bold text-white">We couldn't confirm your payment yet</h1>
            <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-slate-400">
              This usually resolves within a minute. If you were charged, your order is safe — check your email for a
              Stripe receipt, or call us at (507) 841-1801 and we'll confirm manually.
            </p>
            <Link
              to="/pricing"
              data-testid="unconfirmed-pricing-btn"
              className="mt-7 inline-block rounded-full bg-blue-600 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-blue-500"
            >
              Back to Pricing
            </Link>
          </div>
        )}
      </div>
    </main>
  );
}
